import type { ReactNode } from "react";

export interface MatchProps<T> {
  /** The condition to evaluate. This case renders when truthy. */
  when?: T | undefined | null | false;
  /**
   * Value to compare against the parent `Switch`/`AnimatedSwitch` `value` prop.
   * Use instead of `when` for cleaner enum/value-based matching.
   *
   * @example
   * <Switch value={status}>
   *   <Match case="loading"><Spinner /></Match>
   *   <Match case="error"><Error /></Match>
   * </Switch>
   */
  case?: unknown;
  /** Content to render, or a function that receives the truthy `when` value. */
  children: ReactNode | ((item: T) => ReactNode);
  /**
   * Function to call when the previous match is active and rendered.
   * Called after a short delay (~500ms) to preload data for this step.
   * Only supported inside `AnimatedSwitch`.
   */
  preload?: () => void;
}

/**
 * Defines a case within a `Switch` component.
 *
 * `Match` is a marker component that doesn't render anything on its own.
 * It must be used as a direct child of `Switch`, which evaluates each `Match`
 * in order and renders the first one where `when` is truthy.
 *
 * @example
 * <Switch fallback={<DefaultView />}>
 *   <Match when={status === "loading"}>
 *     <Spinner />
 *   </Match>
 *   <Match when={status === "error"}>
 *     <ErrorMessage />
 *   </Match>
 *   <Match when={data}>
 *     {(data) => <DataView data={data} />}
 *   </Match>
 * </Switch>
 *
 * @see {@link Switch} for the parent component
 */
function Match<T>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _props: MatchProps<T>
): ReactNode {
  return null;
}

export default Match;
